import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicEventTicketCompanyManifest } from '../../../services/eventApi';
import LoadingSpinner from '../../Common/LoadingSpinner';

/**
 * Read-only company ticket manifest for batch leaders (Phase 3).
 * Admit remains staff-only — this page is for door reference / print.
 */
const PublicEventTicketCheckInManifestPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getPublicEventTicketCompanyManifest(token);
        if (!cancelled) setData(res.data?.data || null);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.message || 'Manifest not available');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-6 text-center">
          <h1 className="text-lg font-semibold text-gray-900">Manifest unavailable</h1>
          <p className="mt-2 text-sm text-gray-600">{error || 'Invalid link'}</p>
        </div>
      </div>
    );
  }

  const event = data.event || {};
  const counts = data.counts || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-3xl mx-auto px-4 py-8 print:py-4">
        <div className="mb-6">
          <p className="text-xs font-medium uppercase tracking-wide text-indigo-600">Ticket check-in manifest</p>
          <h1 className="mt-1 text-2xl font-semibold text-gray-900">{event.name || 'Event'}</h1>
          {event.code ? <p className="text-sm text-gray-500">{event.code}</p> : null}
          <p className="mt-3 text-base font-medium text-gray-900">{data.externalCompanyName}</p>
          <p className="text-xs text-gray-500 mt-1">
            Read-only list for the batch leader. Door staff admit tickets in the events console.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          <span className="px-2.5 py-1 rounded-md border border-amber-200 bg-amber-50 text-amber-900">
            Pending <strong>{counts.issued || 0}</strong>
          </span>
          <span className="px-2.5 py-1 rounded-md border border-green-200 bg-green-50 text-green-800">
            Checked in <strong>{counts.checkedIn || 0}</strong>
          </span>
          <span className="px-2.5 py-1 rounded-md border border-gray-200 bg-white text-gray-700">
            Total <strong>{counts.total || 0}</strong>
          </span>
        </div>

        <div className="overflow-hidden border border-gray-200 rounded-lg bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Holder</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data.tickets || []).map((t, idx) => (
                <tr key={t._id || t.code}>
                  <td className="px-4 py-2.5 text-sm text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-2.5 text-sm font-mono text-indigo-700">{t.code}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-800">{t.holderName || '—'}</td>
                  <td className="px-4 py-2.5 text-sm">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${
                        t.status === 'CHECKED_IN'
                          ? 'bg-green-50 text-green-800 border-green-200'
                          : t.status === 'ISSUED'
                            ? 'bg-amber-50 text-amber-900 border-amber-200'
                            : 'bg-gray-50 text-gray-700 border-gray-200'
                      }`}
                    >
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 print:hidden">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
          >
            Print manifest
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublicEventTicketCheckInManifestPage;
